import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { z } from "zod";

import type {
  EquipmentSuggestion,
  MaterialFormValues,
  MaterialPayload,
  MaterialResource,
  MaterialUpdatePayload,
} from "../types";
import { useMaterialGroupsQuery } from "../hooks/useMaterialGroupsQuery";
import { useCreateMaterialMutation } from "../hooks/useCreateMaterialMutation";
import { useUpdateMaterialMutation } from "../hooks/useUpdateMaterialMutation";
import stellar from "@/services/stellar";
import { MaterialForm } from "./MaterialForm";

const MATERIAL_FORM_SCHEMA = z.object({
  id: z.string().optional(),
  identifier: z
    .string()
    .optional()
    .transform((value) => value?.trim() ?? ""),
  brand: z
    .string()
    .optional()
    .transform((value) => value?.trim() ?? ""),
  model: z
    .string()
    .optional()
    .transform((value) => value?.trim() ?? ""),
  description: z
    .string({ required_error: "Introduza a descrição do material" })
    .transform((value) => value.trim())
    .refine((value) => value.length > 0, "Introduza a descrição do material"),
  newStock: z.coerce.number().min(0).default(0),
  usedStock: z.coerce.number().min(0).default(0),
  storeBox: z
    .string()
    .optional()
    .transform((value) => value?.trim() ?? ""),
  price: z.coerce.number().min(0).default(0),
  stockMin: z.coerce.number().min(0).default(3),
  materialGroup: z
    .string()
    .optional()
    .transform((value) => value?.trim() ?? ""),
});

type MaterialFormSchema = z.infer<typeof MATERIAL_FORM_SCHEMA>;

type MaterialFormControllerProps = {
  open: boolean;
  mode: "create" | "edit";
  defaultValues: MaterialFormValues;
  simpleMode: boolean;
  onClose: () => void;
  onCancel: () => void;
  onCreated: (material: MaterialResource) => void;
  onUpdated: (material: MaterialResource) => void;
};

const DEFAULT_VALUES: MaterialFormValues = {
  identifier: "",
  brand: "",
  model: "",
  description: "",
  newStock: 0,
  usedStock: 0,
  storeBox: "",
  price: 0,
  stockMin: 3,
  materialGroup: "",
};

const SEARCH_DEBOUNCE = 500;

export const MaterialFormController: React.FC<MaterialFormControllerProps> = ({
  open,
  mode,
  defaultValues,
  simpleMode,
  onClose,
  onCancel,
  onCreated,
  onUpdated,
}) => {
  const descriptionFieldRef = React.useRef<HTMLTextAreaElement | null>(null);
  const { data: materialGroups = [], isPending: isLoadingGroups } =
    useMaterialGroupsQuery();
  const { mutateAsync: createMaterial, isPending: isCreating } =
    useCreateMaterialMutation();
  const { mutateAsync: updateMaterial, isPending: isUpdating } =
    useUpdateMaterialMutation();

  const form = useForm<MaterialFormSchema>({
    resolver: zodResolver(MATERIAL_FORM_SCHEMA),
    defaultValues: { ...DEFAULT_VALUES, ...defaultValues },
  });

  React.useEffect(() => {
    form.reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, form]);

  const [suggestions, setSuggestions] = React.useState<EquipmentSuggestion[]>([]);
  const searchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [isSearching, setIsSearching] = React.useState(false);

  const clearSuggestions = React.useCallback(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    setSuggestions([]);
    setIsSearching(false);
  }, []);

  const fetchSuggestions = React.useCallback((query: string) => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (!query || query.length < 2) {
      clearSuggestions();
      return;
    }

    setIsSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const { equipments } = (await stellar.action("searchEquipment", {
          query,
        })) as { equipments: Array<EquipmentSuggestion & { value?: string }> };

        const sanitized = equipments.map((equipment) => ({
          id: equipment.id,
          brand: equipment.brand,
          model: equipment.model,
        }));

        setSuggestions(sanitized);
      } catch (error) {
        setSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    }, SEARCH_DEBOUNCE);
  }, [clearSuggestions]);

  React.useEffect(() => () => clearSuggestions(), [clearSuggestions]);
  React.useEffect(() => {
    if (!open) {
      clearSuggestions();
    }
  }, [open, clearSuggestions]);

  const handleSuggestionSelect = React.useCallback(
    (suggestion: EquipmentSuggestion) => {
      form.setValue("model", suggestion.model, { shouldDirty: true });
      form.setValue("brand", suggestion.brand, { shouldDirty: true });
      clearSuggestions();

      if (!form.getValues("description") && descriptionFieldRef.current) {
        descriptionFieldRef.current.focus();
      }
    },
    [clearSuggestions, form],
  );

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      const payload: MaterialFormValues = {
        ...DEFAULT_VALUES,
        ...values,
      };

      if (mode === "edit" && payload.id) {
        const updatePayload: MaterialUpdatePayload = {
          ...payload,
          id: payload.id,
        };
        const material = await updateMaterial(updatePayload);
        toast.success("Material atualizado.");
        onUpdated(material);
      } else {
        const { id: _unused, ...creationPayload } = payload;
        const material = await createMaterial(creationPayload as MaterialPayload);
        toast.success("Material criado.");
        onCreated(material);
      }

      form.reset({ ...DEFAULT_VALUES });
      onClose();
    } catch (error) {
      toast.error("Não foi possível guardar o material. Tente novamente.");
    }
  });

  const handleCancel = () => {
    onCancel();
    onClose();
    form.reset({ ...DEFAULT_VALUES, ...defaultValues });
  };

  const isSubmitting = isCreating || isUpdating;

  return (
    <MaterialForm
      open={open}
      mode={mode}
      simpleMode={simpleMode}
      materialGroups={materialGroups as MaterialGroup[]}
      isLoadingGroups={isLoadingGroups}
      isSubmitting={isSubmitting}
      form={form}
      descriptionFieldRef={descriptionFieldRef}
      suggestions={suggestions}
      isSearching={isSearching}
      fetchSuggestions={fetchSuggestions}
      onSuggestionSelect={handleSuggestionSelect}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onClose={onClose}
    />
  );
};
